<script lang="ts">
	import { Helper, Input, Label, Select } from "flowbite-svelte";
	import { getContext } from "svelte";
	import { readable, type Readable } from "svelte/store";
	import { PostalAddressSchema } from "#gen/google/type/postal_address_pb.js";
	import { createUpdatedPostalAddress, states, type PostalAddressField } from "#lib/address.js";
	import type { DataTypeInputProps } from "#lib/components/flow/editor/data-types/inputs/dataTypeInput.js";
	import type { ValidationErrors } from "#lib/components/jsonForms/schema.js";
	import { create, fromJson, toJson } from "#lib/protobuf-compat.js";

	let { value = $bindable(), defaultValue, disabled = false, fullPath = "", oninput, onchange }: DataTypeInputProps = $props();

	const administrativeAreaItems = [{ name: "Select state", value: "" }, ...states];
	const address = $derived(fromJson(PostalAddressSchema, value) ?? create(PostalAddressSchema));

	const defaultAddress = $derived(fromJson(PostalAddressSchema, defaultValue) ?? create(PostalAddressSchema));

	const ajvErrorsStore: Readable<ValidationErrors> = getContext<Readable<ValidationErrors> | undefined>("errors") ?? readable<ValidationErrors>({});

	function displayError(field: PostalAddressField): string | undefined {
		return fullPath ? $ajvErrorsStore[`${fullPath}/${field}`] : undefined;
	}

	function handleAddressLineInput(index: 0 | 1, event: Event) {
		const updatedPostalAddress = createUpdatedPostalAddress(address, "addressLines", (event.target as HTMLInputElement).value, index);
		value = toJson(PostalAddressSchema, updatedPostalAddress);
		oninput?.(event);
	}

	function handleFieldInput(field: "locality" | "postalCode", event: Event) {
		const updatedPostalAddress = createUpdatedPostalAddress(address, field, (event.target as HTMLInputElement).value);
		value = toJson(PostalAddressSchema, updatedPostalAddress);
		oninput?.(event);
	}

	function handleAdministrativeAreaChange(event: Event) {
		const updatedPostalAddress = createUpdatedPostalAddress(address, "administrativeArea", (event.target as HTMLSelectElement).value);
		value = toJson(PostalAddressSchema, updatedPostalAddress);
		onchange?.(event);
	}
</script>

<div class="flex flex-col gap-4" class:opacity-50={disabled}>
	<div class="grid grid-cols-2 gap-4">
		<div class="flex flex-col gap-2">
			<Label for="addressLines">Street address</Label>
			<Input id="addressLines" type="text" autocomplete="address-line1" {disabled} color={displayError("addressLines") ? "red" : "base"} value={address.addressLines[0] ?? ""} placeholder={defaultAddress.addressLines[0] ?? ""} on:input={e => handleAddressLineInput(0, e)} on:change={e => onchange?.(e)} />
			{#if displayError("addressLines")}
				<Helper color="red">{displayError("addressLines")}</Helper>
			{/if}
		</div>
		<div class="flex flex-col gap-2">
			<Label for="addressLines2">Apt, suite, etc.</Label>
			<Input id="addressLines2" type="text" autocomplete="address-line2" {disabled} color="base" value={address.addressLines[1] ?? ""} placeholder={defaultAddress.addressLines[1] ?? ""} on:input={e => handleAddressLineInput(1, e)} on:change={e => onchange?.(e)} />
		</div>
	</div>
	<div class="grid grid-cols-3 gap-4">
		<div class="flex flex-col gap-2">
			<Label for="locality">City</Label>
			<Input id="locality" type="text" autocomplete="address-level2" {disabled} color={displayError("locality") ? "red" : "base"} value={address.locality ?? ""} placeholder={defaultAddress.locality ?? ""} on:input={e => handleFieldInput("locality", e)} on:change={e => onchange?.(e)} />
			{#if displayError("locality")}
				<Helper color="red">{displayError("locality")}</Helper>
			{/if}
		</div>
		<div class="flex flex-col gap-2">
			<Label for="administrativeArea">State</Label>
			<Select id="administrativeArea" {disabled} color={displayError("administrativeArea") ? "red" : "base"} value={address.administrativeArea ?? ""} placeholder={defaultAddress.administrativeArea ?? ""} items={administrativeAreaItems} on:change={handleAdministrativeAreaChange} />
			{#if displayError("administrativeArea")}
				<Helper color="red">{displayError("administrativeArea")}</Helper>
			{/if}
		</div>
		<div class="flex flex-col gap-2">
			<Label for="postalCode">Zip</Label>
			<Input id="postalCode" type="text" autocomplete="postal-code" {disabled} color={displayError("postalCode") ? "red" : "base"} value={address.postalCode ?? ""} placeholder={defaultAddress.postalCode ?? ""} on:input={e => handleFieldInput("postalCode", e)} on:change={e => onchange?.(e)} />
			{#if displayError("postalCode")}
				<Helper color="red">{displayError("postalCode")}</Helper>
			{/if}
		</div>
	</div>
</div>
